// ─── Pcs belum tertagih per KELUARGA pecah-baris (mig 8340, bughunt G8-1) ────
//
// Cermin klien helper SQL `order_item_family_open_pcs` (mig 8340 §1a) — SATU
// rumus untuk semua cermin "pcs tertagih" di klien:
//   • basis 'dp'   → `compute_order_dp_due` (Wajib DP Keranjang pembeli & admin,
//                    `dpIssueBlockReason`, kartu Rincian DP);
//   • basis 'free' → `order_item_free_pcs` (pcs bebas `itemFreePcs`: Buat Nota,
//                    Request Kirim tagih ikut, batal sebagian, Buat Tagihan massal).
// Geser keduanya bersama; `itemFamilyOpen.test.ts` mengunci urutan & angkanya.
//
// KENAPA PER KELUARGA. Pecah-baris di nota yang belum lunas meninggalkan klaim
// lunas di baris nota SUMBER (mig 8340 §1), dan pecah-baris sebelum mig 7910
// meninggalkan keadaan yang sama. Baris sumber lalu mengklaim lebih dari isinya
// (saldo negatif) sementara pecahannya tampak tanpa klaim. Rumus per baris yang
// menjepit nol membuang kelebihan itu: pcs pecahan terbaca "belum ditagih" (DP
// membengkak) atau "bebas" (Buat Nota kedua untuk pcs yang sama).
//
// ATURAN (persis SQL):
//   • keluarga = akar lewat `sourceItemId` di dalam pesanan yang sama; baris yang
//     induknya tak ada di himpunan (atau di pesanan lain) adalah akar;
//   • saldo negatif baris aktif dijumlah per keluarga (= kelebihan klaim) dan
//     diserap baris aktif bersaldo positif berurutan `depth desc, createdAt desc,
//     id desc` — pecahan terdalam dulu;
//   • baris `cancelled` tak menyerap & tak menyumbang (paritas mig 8000): pcs
//     belum tertagihnya = saldonya sendiri dijepit nol;
//   • tanpa saldo negatif hasilnya = rumus per baris lama.

/** Satu baris pesanan untuk penghitungan keluarga. */
export type FamilyItem = {
  id: string;
  /** `order_items.source_item_id` — induk pecah-baris; null = baris asli. */
  sourceItemId?: string | null;
  /** `order_items.order_id` — keluarga tak keluar dari pesanannya. */
  orderId?: string | null;
  /** `order_items.created_at` (teks timestamptz PostgREST, presisi mikrodetik). */
  createdAt?: string | null;
  qty: number;
  paidQty?: number | null;
  keepStatus?: string | null;
};

/** Baris + saldonya (boleh negatif) — masukan {@link familyOpenPcs}. */
export type FamilyBalanceRow = {
  id: string;
  sourceItemId?: string | null;
  orderId?: string | null;
  createdAt?: string | null;
  cancelled: boolean;
  /** Basis 'dp': qty − klaim; basis 'free': qty − paid_qty − klaim belum lunas. */
  balance: number;
};

const MAX_DEPTH = 64;

/**
 * Kunci urut `created_at` setara timestamptz Postgres: milidetik epoch + sisa
 * mikrodetik (Date.parse membuang digit ke-4…6, padahal pecahan lahir di +1 µs).
 * Tak terbaca = paling awal.
 */
function createdAtKey(raw: string | null | undefined): [number, number] {
  if (!raw) return [Number.NEGATIVE_INFINITY, 0];
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return [Number.NEGATIVE_INFINITY, 0];
  const frac = /:\d{2}\.(\d+)/.exec(raw)?.[1] ?? "";
  const micro = Number((frac + "000000").slice(3, 6));
  return [ms, Number.isFinite(micro) ? micro : 0];
}

function wholeInt(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/**
 * Pcs belum tertagih per baris sesudah kelebihan klaim keluarga diserap — cermin
 * kolom `open_pcs` `order_item_family_open_pcs`. Kunci = `id` setiap baris masukan.
 */
export function familyOpenPcs(rows: readonly FamilyBalanceRow[]): Map<string, number> {
  const byId = new Map<string, FamilyBalanceRow>();
  for (const r of rows) byId.set(r.id, r);

  const parentOf = (r: FamilyBalanceRow): FamilyBalanceRow | null => {
    const src = r.sourceItemId ?? null;
    if (!src) return null;
    const p = byId.get(src);
    if (!p) return null;
    if ((p.orderId ?? null) !== (r.orderId ?? null)) return null;
    return p;
  };

  const groups = new Map<string, { row: FamilyBalanceRow; depth: number }[]>();
  for (const r of byId.values()) {
    let root = r;
    let depth = 0;
    for (let p = parentOf(root); p && depth < MAX_DEPTH; p = parentOf(root)) {
      root = p;
      depth += 1;
    }
    const list = groups.get(root.id) ?? [];
    list.push({ row: r, depth });
    groups.set(root.id, list);
  }

  const out = new Map<string, number>();
  for (const members of groups.values()) {
    let deficit = 0;
    for (const { row } of members) {
      if (!row.cancelled) deficit += Math.max(0, -wholeInt(row.balance));
    }
    const ordered = [...members].sort((a, b) => {
      if (a.depth !== b.depth) return b.depth - a.depth;
      const ka = createdAtKey(a.row.createdAt);
      const kb = createdAtKey(b.row.createdAt);
      if (ka[0] !== kb[0]) return ka[0] < kb[0] ? 1 : -1;
      if (ka[1] !== kb[1]) return kb[1] - ka[1];
      return a.row.id < b.row.id ? 1 : a.row.id > b.row.id ? -1 : 0;
    });
    let before = 0;
    for (const { row } of ordered) {
      const own = Math.max(0, wholeInt(row.balance));
      if (row.cancelled) {
        out.set(row.id, own);
        continue;
      }
      const absorbed = Math.min(own, Math.max(0, deficit - before));
      out.set(row.id, own - absorbed);
      before += own;
    }
  }
  return out;
}

function isCancelled(item: FamilyItem): boolean {
  return item.keepStatus === "cancelled";
}

/**
 * Basis 'dp' — pcs per baris yang SUDAH ditagih di tagihan barang hidup, per
 * keluarga: `qty − open_pcs`. `claimedByItem` = Σ qty klaim mentah per baris
 * (`buildCartInvoiceIndex().billedByItem` / `dpBilledQtyFromLines`, rumus 8000).
 * Hasilnya disuapkan sebagai `billedQty` ke `computeOrderDpDue`,
 * `buildDpBreakdown` dan `deriveCartBadge` — sama dengan `compute_order_dp_due`
 * dan `billed_qty` nota publik (mig 8340 §1b/§1d).
 */
export function dpBilledQtyByItem(
  items: readonly FamilyItem[],
  claimedByItem: ReadonlyMap<string, number>
): Map<string, number> {
  const open = familyOpenPcs(
    items.map((it) => ({
      id: it.id,
      sourceItemId: it.sourceItemId ?? null,
      orderId: it.orderId ?? null,
      createdAt: it.createdAt ?? null,
      cancelled: isCancelled(it),
      balance: wholeInt(it.qty) - Math.max(0, wholeInt(claimedByItem.get(it.id) ?? 0))
    }))
  );
  const out = new Map<string, number>();
  for (const it of items) {
    out.set(it.id, Math.max(0, wholeInt(it.qty) - (open.get(it.id) ?? 0)));
  }
  return out;
}

/**
 * Basis 'free' — `reservedQty` per baris yang membuat `itemFreePcs` (qty −
 * paid − reserved, dijepit nol) sama dengan `order_item_free_pcs` per keluarga:
 * baris penyerap mendapat tambahan sebesar kelebihan klaim yang diserapnya; baris
 * lain tetap `reservedByItem` apa adanya (baris bersaldo negatif sudah nol bebas).
 */
export function familyReservedQtyByItem(
  items: readonly FamilyItem[],
  reservedByItem: ReadonlyMap<string, number>
): Map<string, number> {
  const balanceOf = (it: FamilyItem) =>
    wholeInt(it.qty) - wholeInt(it.paidQty ?? 0) - wholeInt(reservedByItem.get(it.id) ?? 0);
  const open = familyOpenPcs(
    items.map((it) => ({
      id: it.id,
      sourceItemId: it.sourceItemId ?? null,
      orderId: it.orderId ?? null,
      createdAt: it.createdAt ?? null,
      cancelled: isCancelled(it),
      balance: balanceOf(it)
    }))
  );
  const out = new Map<string, number>();
  for (const it of items) {
    const reserved = wholeInt(reservedByItem.get(it.id) ?? 0);
    const own = Math.max(0, balanceOf(it));
    const absorbed = own - (open.get(it.id) ?? own);
    out.set(it.id, reserved + Math.max(0, absorbed));
  }
  return out;
}

/** Baris `order_items` mentah PostgREST (snake_case) → {@link FamilyItem}. */
export function familyItemFromRow(row: any, orderId?: string | null): FamilyItem {
  return {
    id: String(row?.id ?? ""),
    sourceItemId: (row?.source_item_id as string | null | undefined) ?? null,
    orderId: orderId ?? (row?.order_id as string | null | undefined) ?? null,
    createdAt: (row?.created_at as string | null | undefined) ?? null,
    qty: Number(row?.qty ?? 0),
    paidQty: row?.paid_qty != null ? Number(row.paid_qty) : 0,
    keepStatus: (row?.keep_status as string | null | undefined) ?? null
  };
}
